import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, TrendingUp, Handshake, Banknote, ArrowRight, MapPin, Star } from "lucide-react";
import { api } from "@/lib/api";
import ProjectCard from "@/components/ProjectCard";
import EMICalc from "@/components/EMICalc";
import InquiryForm from "@/components/InquiryForm";

const HERO_IMG = "https://images.unsplash.com/photo-1779976955613-b74623824d1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjByZXNpZGVudGlhbCUyMGFwYXJ0bWVudCUyMGJ1aWxkaW5nJTIwZXh0ZXJpb3IlMjBpbmRpYXxlbnwwfHx8fDE3ODM1MDk2MzB8MA&ixlib=rb-4.1.0&q=85";
const AGENT_IMG = "https://customer-assets-v7afamib.emergentagent.net/job_ce2eeba2-d7a3-4e66-903a-a522eae9ede4/artifacts/y2crjft4_ChatGPT%20Image%20Aug%2022%2C%202026%2C%2008_00_31%20PM.png";

const LOCATIONS = ["Kompally", "Bachupally", "Nagaram", "Uppal", "LB Nagar", "Adibatla", "Shadnagar", "Shamshabad"];

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({ live_projects: null, bank_partners: null });

  useEffect(() => {
    api.get("/projects", { params: { featured: true } })
      .then((r) => setFeatured(r.data))
      .catch(() => setFeatured([]));
    api.get("/public/stats")
      .then((r) => setStats(r.data))
      .catch(() => setStats({ live_projects: null, bank_partners: null }));
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest/85 via-forest/60 to-forest/30" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-32">
          <div className="max-w-3xl text-white">
            <div className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur border border-white/20 px-3 py-1.5 text-xs mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-gold" />
              Verified Builders · Hyderabad
            </div>
            <h1 className="font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              Your first home,<br />done <span className="text-gold">right</span> the first time.
            </h1>
            <p className="mt-6 text-base md:text-lg text-white/85 max-w-xl leading-relaxed">
              Your first home, priced like it should be.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/projects" data-testid="hero-cta-projects" className="inline-flex items-center gap-2 rounded-full bg-gold hover:bg-gold-hover text-forest px-6 py-3 text-sm font-semibold transition-all">
                Browse Verified Projects <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/emi" data-testid="hero-cta-emi" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/25 text-white px-6 py-3 text-sm font-medium">
                Check Loan Eligibility
              </Link>
            </div>

            <div className="mt-10 flex flex-wrap gap-8" data-testid="hero-stats">
              {[
                { n: stats.live_projects != null ? `${stats.live_projects}+` : "—", l: "Live Projects", testid: "stat-live-projects" },
                { n: stats.bank_partners != null ? `${stats.bank_partners}`  : "—", l: "Bank Tie-ups",  testid: "stat-bank-tieups" },
                { n: "Zero", l: "Hidden Charges", testid: "stat-hidden-charges" },
              ].map((s) => (
                <div key={s.l} data-testid={s.testid}>
                  <div className="font-heading text-2xl md:text-3xl font-bold text-gold">{s.n}</div>
                  <div className="text-xs uppercase tracking-wider text-white/70 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="border-y border-border bg-cream">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 grid gap-6 md:grid-cols-2 items-center">
          {[
            { icon: Handshake, t: "Property search to keys", d: "You search. We handle everything else — all the way to your keys." },
            { icon: Banknote, t: "Loan Made Easy", d: "Backed by leading banks, not just us." },
          ].map((f, i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center flex-none">
                <f.icon className="w-5 h-5 text-forest" />
              </div>
              <div>
                <div className="font-heading font-bold text-forest text-lg">{f.t}</div>
                <div className="text-sm text-muted-foreground mt-0.5">{f.d}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Featured projects */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gold font-semibold mb-3">
              <Star className="w-3.5 h-3.5 fill-gold" /> Featured
            </div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-forest tracking-tight">Hand-picked, verified projects</h2>
            <p className="text-muted-foreground text-sm mt-2 max-w-xl">Every project below has been walked, checked, and cleared by our team. Price shown upfront — no games.</p>
          </div>
          <Link to="/projects" data-testid="featured-view-all" className="text-sm font-medium text-forest hover:text-gold-hover flex items-center gap-1">
            View all <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 stagger">
          {featured.map((p) => (
            <ProjectCard key={p.id} project={p} />
          ))}
          {featured.length === 0 && (
            <div className="col-span-full text-center py-16 text-muted-foreground text-sm">Projects loading…</div>
          )}
        </div>
      </section>

      {/* How we verify every project */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 md:pb-20" data-testid="how-we-verify-section">
        <div className="rounded-2xl bg-cream border border-border p-8 md:p-10">
          <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold mb-3">Transparency</div>
          <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-forest tracking-tight leading-tight">
            How we check every project before listing it
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div>
              <div className="text-2xl font-heading font-extrabold text-gold">01</div>
              <div className="font-heading font-bold text-forest text-lg mt-1">We see it before you do.</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                We walk every project ourselves, so what&apos;s promised is what&apos;s actually there.
              </p>
            </div>
            <div>
              <div className="text-2xl font-heading font-extrabold text-gold">02</div>
              <div className="font-heading font-bold text-forest text-lg mt-1">We only list what&apos;s worth the price.</div>
              <p className="text-sm text-muted-foreground mt-1.5 leading-relaxed">
                Every home is checked to be a smart buy — not just a good listing photo.
              </p>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-border/70 font-heading font-bold text-forest">
            Seen in person, priced fairly. That&apos;s the whole checklist.
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="bg-forest text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold mb-3">Coverage</div>
              <h2 className="font-heading font-extrabold text-3xl md:text-4xl tracking-tight leading-tight">
                Hyderabad's fastest-growing corridors, all in one place.
              </h2>
              <p className="text-white/70 mt-4 text-sm max-w-md leading-relaxed">
                Apartments and independent houses across budgets you can actually plan around.
              </p>
              <div className="mt-3 text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">
                Sized for real budgets, built for real Hyderabad families.
              </div>
            </div>
            <div className="flex flex-wrap gap-2.5">
              {LOCATIONS.map((l) => (
                <Link
                  key={l}
                  to={`/projects?location=${encodeURIComponent(l)}`}
                  data-testid={`location-chip-${l.toLowerCase().replace(/\s/g, "-")}`}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white/5 hover:bg-gold hover:text-forest border border-white/15 px-4 py-2 text-sm transition-all"
                >
                  <MapPin className="w-3.5 h-3.5" /> {l}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* EMI + Inquiry */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 md:py-24">
        <div className="grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <div className="text-[11px] uppercase tracking-[0.2em] text-gold font-semibold mb-3">Finance</div>
            <h2 className="font-heading font-extrabold text-3xl md:text-4xl text-forest tracking-tight leading-tight">
              Know your EMI before you fall in love with a home.
            </h2>
            <p className="text-muted-foreground text-sm mt-2 max-w-lg">
              We work with multiple banks & NBFCs. Get pre-approved in 48 hours.
            </p>
            <div className="mt-8">
              <EMICalc />
            </div>
          </div>
          <div className="lg:col-span-2">
            <InquiryForm />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 md:pb-24">
        <div className="rounded-2xl bg-cream border border-border p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center overflow-hidden relative">
          <div className="relative z-10">
            <h3 className="font-heading font-extrabold text-3xl text-forest tracking-tight leading-tight">
              Every home, walked in person.
            </h3>
            <p className="text-muted-foreground text-sm mt-3 max-w-md">
              Every price, one that makes sense. No shortcuts. Ever.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/projects" className="inline-flex items-center gap-2 rounded-full bg-forest text-white hover:bg-forest-hover px-5 py-2.5 text-sm font-medium">
                See what we&apos;ve cleared
              </Link>
            </div>
          </div>
          <div className="relative">
            <img src={AGENT_IMG} alt="Our First Realty team" className="rounded-xl w-full object-cover aspect-[4/3]" />
          </div>
        </div>
      </section>
    </div>
  );
}
